import React, { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import Button from '@/components/ui/Button';
import { Icon } from '@iconify/react';

export type DropdownItem = {
  label: string;
  icon?: string;
  onClick?: () => void;
  destructive?: boolean;
  success?: boolean;
};

interface Props {
  items: DropdownItem[];
  triggerVariant?: 'primary' | 'secondary' | 'outline' | 'ghost';
  triggerSize?: 'sm' | 'md' | 'lg' | 'xl';
  triggerClassName?: string;
  label?: React.ReactNode;
  icon?: string;
}

const DropdownButton: React.FC<Props> = ({
  items,
  triggerVariant = 'ghost',
  triggerSize = 'md',
  triggerClassName,
  label,
  icon,
}) => {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement | null>(null);
  const buttonRef = useRef<HTMLButtonElement | null>(null);
  const menuRef = useRef<HTMLDivElement | null>(null);
  const [menuStyle, setMenuStyle] = useState<{ top: number; left: number } | null>(null);

  useEffect(() => {
    function onDoc(e: MouseEvent) {
      const target = e.target as Node;
      const clickedInsideTrigger = buttonRef.current?.contains(target);
      const clickedInsideMenu = menuRef.current?.contains(target);
      if (!clickedInsideTrigger && !clickedInsideMenu) setOpen(false);
    }
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, []);

  // Recalculate menu position when opened, on resize or scroll
  useEffect(() => {
    if (!open) {
      setMenuStyle(null);
      return;
    }

    function updatePosition() {
      const btn = buttonRef.current;
      if (!btn) return;
      const rect = btn.getBoundingClientRect();
      const menuWidth = 192; // matches w-48 (12rem)
      // Position below the button, align right edge of menu with button right edge
      let left = rect.right - menuWidth;
      // fallback to left edge of button if menu would overflow on the left
      if (left < 8) left = rect.left;
      const top = rect.bottom + 8; // small gap
      setMenuStyle({ top: Math.max(8, Math.round(top)), left: Math.max(8, Math.round(left)) });
    }

    updatePosition();
    window.addEventListener('resize', updatePosition);
    window.addEventListener('scroll', updatePosition, true);
    return () => {
      window.removeEventListener('resize', updatePosition);
      window.removeEventListener('scroll', updatePosition, true);
    };
  }, [open]);

  return (
    <div className="relative" ref={ref}>
      <Button
        onClick={() => setOpen((v) => !v)}
        variant={triggerVariant}
        size={triggerSize}
        className={triggerClassName}
        aria-expanded={open}
        aria-haspopup="true"
        // expose underlying button for positioning
        ref={buttonRef as any}
      >
        {label ? label : <Icon icon={icon || 'mdi:dots-vertical'} className="w-5 h-5" />}
      </Button>

      {open && menuStyle
        ? createPortal(
            <div
              ref={menuRef}
              style={{ position: 'fixed', top: menuStyle.top, left: menuStyle.left, width: 192 }}
              className="bg-white rounded-md shadow-lg z-50 overflow-hidden"
            >
              {items.map((it, idx) => (
                <>
                  <button
                    key={idx}
                    onClick={() => {
                      setOpen(false);
                      it.onClick?.();
                    }}
                    className={`w-full text-left px-4 py-3 flex items-center gap-3 text-sm hover:bg-gray-50 ${
                      it.destructive
                        ? 'text-red-600'
                        : it.success
                          ? 'text-green-600'
                          : 'text-gray-700'
                    }`}
                  >
                    {it.icon && <Icon icon={it.icon} className="text-lg" />}
                    <span>{it.label}</span>
                  </button>
                </>
              ))}
            </div>,
            document.body
          )
        : null}
    </div>
  );
};

export default DropdownButton;
